import { addLocale, locale, type LocaleOptions } from "primereact/api";
import "../i18n";

import { ConfirmPopup } from "primereact/confirmpopup";
import { Toast } from "primereact/toast";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router-dom";
import { useError } from "src/core/hooks/useError";
import { useGtm } from "src/core/hooks/useGtm";
import { ToastContext } from "../core/context/ToastContext";
import { useErrorHandler } from "../core/error/useErrorHandler";
import { useSettingsStore } from "../features/settings/services/useSettingsStore";
import "./App.css";
import { AppContent } from "./components/AppContent/AppContent";
import AppFooter from "./components/AppFooter/AppFooter";
import AppHeader from "./components/AppHeader/AppHeader";

function App() {
  const toastRef = useRef<Toast>(null);

  const { i18n, t } = useTranslation();
  const { getErrorMessage } = useError();

  const gtm = useGtm();
  const settingsStore = useSettingsStore();
  const errorHandler = useErrorHandler();

  const loadLocale = () => {
    const options = t("primereact", { returnObjects: true }) as LocaleOptions;
    addLocale(i18n.language, options);
    locale(i18n.language);
  };

  loadLocale();

  useEffect(() => {
    const browserLang = navigator.language.split("-")[0];
    const supportedLangs = ["hu", "en"];
    const lang = supportedLangs.includes(browserLang) ? browserLang : "en";
    i18n.changeLanguage(lang);

    settingsStore.fetchSettings().catch((error) => {
      errorHandler.setError({
        title: t("Base.Error"),
        message: "fetchSettings: " + getErrorMessage(error),
      });
    });
  }, []);

  useEffect(() => {
    const gtmId = settingsStore.clientConfiguration.gtmId;

    if (gtmId) {
      gtm.init(gtmId);
    }
  }, [settingsStore.clientConfiguration.gtmId]);

  useEffect(() => {
    loadLocale();
  }, [i18n.language, t]);

  useEffect(() => {
    if (errorHandler.message) {
      toastRef.current?.show({
        severity: "error",
        summary: errorHandler.title,
        detail: errorHandler.message,
      });
      errorHandler.clearError();
    }
  }, [errorHandler]);

  const base = import.meta.env.VITE_BASE_URL || "/";

  return (
    <BrowserRouter basename={base}>
      <ToastContext.Provider value={toastRef}>
        <Toast
          ref={toastRef}
          position="bottom-right"
        />

        <ConfirmPopup />

        <div className="scrollTop"></div>

        <div className="app flex flex-column h100 w100 overflow-y-auto overflow-x-hidden align-items-center">
          <AppHeader />
          <AppContent />
          <AppFooter />
        </div>
      </ToastContext.Provider>
    </BrowserRouter>
  );
}

export default App;
